import { useRef } from "react"
import { useToggle } from "react-use"

import style from "modules/PsyProfile/components/Info/components/Labels/style.module.scss"
import { Psychologist } from "utils/interfaces"
import { useDidMount } from "utils/customHooks"
import { FriendlyLabel, FriendlyLabelsStorage } from "components/Labels"
import { numberToYearsInRussian } from "utils/infrastructure/wording-utils"
import { SessionCountLabel } from "modules/PsyCommon/components"
import { Img } from "ui-kit/components-branded/Img"
import { Text } from "ui-kit/components-branded/Text"
import { HelpTooltip } from "components"

interface LabelsProps {
    psy: Psychologist
}

export function Labels({ psy }: LabelsProps) {
    const [, toggleOverflowX] = useToggle(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const scrollListener = () => {
        const element = containerRef.current
        if (element) {
            if (Math.round(element.offsetWidth + element.scrollLeft) >= element.scrollWidth) {
                toggleOverflowX(false)
            } else {
                toggleOverflowX(true)
            }
        }
    }

    useDidMount(() => {
        const element = containerRef.current
        if (element) {
            if (element.offsetWidth < element.scrollWidth) {
                toggleOverflowX(true)
            }
            element.addEventListener("scroll", scrollListener)

            return () => element.removeEventListener("scroll", scrollListener)
        }
    })

    const activeMembership = (
        <>
            Психолог активно участвует в&nbsp;жизни профессионального сообщества Alter, выполняя
            одну или несколько ролей: ведёт интервизионные и&nbsp;супервизорские группы; проводит
            вебинары или спецпроекты; регулярно посещает мероприятия для повышения квалификации;
            создает информационный контент для клиентов; участвует в&nbsp;коллегиальных обсуждениях
            профессиональных вопросов; поддерживает волонтерские инициативы сообщества.
        </>
    )

    if (
        psy.working_with?.adults.foreigner ||
        psy.experience_category ||
        psy.alter_experience ||
        psy.active_community_member ||
        (FriendlyLabelsStorage.isAnyLabelEnabled &&
            (psy.working_with?.adults.friendly ||
                psy.working_with?.adults.believer ||
                psy.working_with?.adults.fem))
    ) {
        return (
            <div ref={containerRef} data-testid="psy-profile-labels">
                <div className={style.content}>
                    {psy.experience_category && (
                        <SessionCountLabel
                            category={psy.experience_category}
                            className={style.label}
                        />
                    )}
                    {psy.alter_experience !== null && psy.alter_experience >= 1 && (
                        <div className={style.label}>
                            <Img src="green-heart-emoji.png" className={style.emoji} />
                            {psy.alter_experience}&nbsp;
                            {numberToYearsInRussian(psy.alter_experience)} в&nbsp;Alter
                        </div>
                    )}
                    <FriendlyLabel psy={psy} />
                    {psy.active_community_member && (
                        <div className={style.label} data-testid="active-member">
                            <Img src="active-member.png" className={style.emoji} />
                            Активный участник сообщества
                            <HelpTooltip
                                placement="bottom"
                                iconStyles={{
                                    size: 16,
                                }}
                                className={style.activeMemberTooltip}
                                iconWrapperClassName={style.helpIconWrapper}
                                overlay={
                                    <div data-testid="psy-profile-active-member">
                                        <Text isSecondary color="secondary">
                                            {activeMembership}
                                        </Text>
                                    </div>
                                }
                            />
                        </div>
                    )}
                    {psy.working_with?.adults.foreigner && (
                        <div className={style.label}>
                            <Img src="globe-emoji.png" className={style.emoji} />
                            Сессии на английском
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return null
}
